import {
  Button,
  Flex,
  Popover,
  PopoverAnchor,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  Portal,
  Text,
  Textarea,
} from "@chakra-ui/react";
import React from "react";

// Draft comment editor anchored to a question block. The draft only lives in the review session
// until "Reopen for RN" sends it.
// Typed text is never lost by accident: when the popover closes any other way than Cancel / Escape
// / Remove / saving (a click outside, opening another question's popover, the review refreshing),
// the text is kept as the question's draft (`onAutoSave`). That happens once the popover has
// finished closing, so the dirty text is also reported as it changes (`onDirtyTextChange`): the
// review counts it as unsent work when it is closed while the popover is still open.

/** Escape inside a popover/dialog must not also reach the review modal (it would close it). */
export function stopEscapePropagation(event: React.KeyboardEvent) {
  if (event.key === "Escape") {
    event.stopPropagation();
  }
}

interface Props {
  /** The question block (must forward its ref): the popover is anchored to it. */
  children: React.ReactElement;
  draftText: string | null;
  isOpen: boolean;
  questionLabel: string;
  onClose: () => void;
  /** Keeps dirty text as the draft when the popover closes without an explicit choice. */
  onAutoSave: (text: string) => void;
  /** The trimmed text not yet kept as the draft, or null (nothing new / handled / closed). */
  onDirtyTextChange: (text: string | null) => void;
  onRemove: () => void;
  onSave: (text: string) => void;
}

export default function CommentPopover(props: Props) {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  return (
    <Popover
      isLazy
      closeOnBlur={true}
      initialFocusRef={textareaRef}
      isOpen={props.isOpen}
      placement="bottom-end"
      onClose={props.onClose}
    >
      <PopoverAnchor>{props.children}</PopoverAnchor>
      <Portal>
        <PopoverContent boxShadow="lg" w="360px" onKeyDown={stopEscapePropagation}>
          <PopoverArrow />
          <PopoverBody p={3}>
            <CommentForm
              draftText={props.draftText}
              questionLabel={props.questionLabel}
              textareaRef={textareaRef}
              onAutoSave={props.onAutoSave}
              onCancel={props.onClose}
              onDirtyTextChange={props.onDirtyTextChange}
              onRemove={props.onRemove}
              onSave={props.onSave}
            />
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}

function CommentForm(props: {
  draftText: string | null;
  questionLabel: string;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  onAutoSave: (text: string) => void;
  onCancel: () => void;
  onDirtyTextChange: (text: string | null) => void;
  onRemove: () => void;
  onSave: (text: string) => void;
}) {
  // Mounted per opening (isLazy), so the text starts from the current draft every time.
  const [text, setText] = React.useState(props.draftText ?? "");
  const isEmpty = text.trim() === "";
  const isUnchanged = text.trim() === (props.draftText ?? "").trim();
  const canSave = !isEmpty && !isUnchanged;

  // Read on unmount: the latest text, whether the reviewer already chose what to do with it.
  const latestRef = React.useRef({
    canSave,
    text,
    onAutoSave: props.onAutoSave,
    onDirtyTextChange: props.onDirtyTextChange,
  });
  latestRef.current = {
    canSave,
    text,
    onAutoSave: props.onAutoSave,
    onDirtyTextChange: props.onDirtyTextChange,
  };
  const isHandledRef = React.useRef(false);

  const dirtyText = canSave ? text.trim() : null;
  React.useEffect(() => {
    if (!isHandledRef.current) {
      latestRef.current.onDirtyTextChange(dirtyText);
    }
  }, [dirtyText]);

  React.useEffect(
    () => () => {
      const latest = latestRef.current;
      if (!isHandledRef.current && latest.canSave) {
        latest.onAutoSave(latest.text.trim());
      }
      latest.onDirtyTextChange(null);
    },
    []
  );

  const markHandled = () => {
    isHandledRef.current = true;
    props.onDirtyTextChange(null);
  };

  const handle = (action: () => void) => {
    markHandled();
    action();
  };

  const save = () => {
    if (canSave) {
      handle(() => props.onSave(text.trim()));
    }
  };

  return (
    <Flex
      direction="column"
      gap={2}
      onKeyDown={(event) => {
        // Escape means cancel (Chakra closes the popover on the same event)
        if (event.key === "Escape") {
          markHandled();
        }
      }}
    >
      <Text color="gray.500" fontSize="xs" noOfLines={2}>
        Comment for the RN on <b>{props.questionLabel}</b>
      </Text>
      <Textarea
        ref={props.textareaRef}
        placeholder="What should the RN fix?"
        rows={3}
        size="sm"
        value={text}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            save();
          }
        }}
      />
      <Text color="gray.400" fontSize="xs">
        Saved as a draft. It is sent when you reopen the document for the RN.
      </Text>
      <Flex gap={2} justify="flex-end">
        {props.draftText !== null && (
          <Button
            colorScheme="red"
            mr="auto"
            size="sm"
            variant="ghost"
            onClick={() => handle(props.onRemove)}
          >
            Remove
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => handle(props.onCancel)}>
          Cancel
        </Button>
        <Button colorScheme="orange" isDisabled={!canSave} size="sm" onClick={save}>
          {props.draftText === null ? "Add comment" : "Update comment"}
        </Button>
      </Flex>
    </Flex>
  );
}
